import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";

export default function BatchDeletePathwayTypesDialog({
  open,
  onOpenChange,
  pathwayTypes,
  onDeleted,
}) {
  const [loading, setLoading] = useState(false);
  const { getToken } = useAuth();

  useEffect(() => {
    if (!open) return;
    setLoading(false);
  }, [open]);

  const selected = useMemo(() => {
    const rows = Array.isArray(pathwayTypes) ? pathwayTypes : [];
    return rows.filter(Boolean);
  }, [pathwayTypes]);

  const levelIds = useMemo(() => {
    const ids = [];
    const seen = new Set();
    for (const row of selected) {
      const id = row?.id ?? row?.level_id ?? row?.levelId;
      const n = Number(id);
      if (!Number.isFinite(n)) continue;
      const key = String(n);
      if (seen.has(key)) continue;
      seen.add(key);
      ids.push(n);
    }
    return ids;
  }, [selected]);

  const selectedCount = selected.length;

  const handleDelete = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: {
          level_ids: levelIds,
        },
      };

      const res = await axios.delete(
        api_endpoint + "/pathway/levels/delete/bulk",
        reqConfig
      );

      const data = res?.data;
      const totalDeleted =
        data?.total_deleted != null ? Number(data.total_deleted) : null;
      const totalErrors =
        data?.total_errors != null ? Number(data.total_errors) : null;

      const deletedCount = Number.isFinite(totalDeleted)
        ? totalDeleted
        : Array.isArray(data?.deleted_level_ids)
          ? data.deleted_level_ids.length
          : 0;

      if (Number.isFinite(totalErrors) && totalErrors > 0) {
        toast.success(
          `Deleted ${deletedCount} pathway type${deletedCount === 1 ? "" : "s"}. ${totalErrors} failed.`
        );
      } else {
        toast.success(
          `Deleted ${deletedCount} pathway type${deletedCount === 1 ? "" : "s"} successfully!`
        );
      }

      onOpenChange(false);
      if (onDeleted) onDeleted(data);
    } catch (error) {
      console.error("Failed to batch delete pathway types", error);
      toast.error("Failed to delete pathway types.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const canDelete = !loading && levelIds.length >= 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md h-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-(--neutral-900)">
            Delete Pathway Types
          </DialogTitle>
        </DialogHeader>
        <div className="font-body text-(--neutral-800)">
          Are you sure you want to delete <b>{selectedCount}</b> pathway type{selectedCount === 1 ? "" : "s"}? This action cannot be undone.
        </div>
        <DialogFooter className="flex gap-2 justify-between">
          <DialogClose asChild>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className=""
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant=""
            onClick={handleDelete}
            disabled={!canDelete}
            className=""
          >
            {loading ? (
              <span className="flex items-center justify-center w-full">
                <ClipLoader size={20} color="#fff" />
              </span>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
